import React, { useState } from 'react';
import Selection from 'src/components/Selection/Selection';
import classNames from 'classnames';
import Loading from '../../../../Loading';
import { observer } from 'src/utils/mobx-react';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { action, computed, makeAutoObservable } from 'mobx';
import { CompetitionModel } from 'src_common/common/websocket2/models/CompetitionModel';
import { useAppStateContext } from 'src/AppState/AppState';
import { TrapChallengeState } from './TrapChallenge.state';

type EventPropsType = {
    userId: number;
    event: EventModel;
    onHeaderClick: (event: EventModel | null) => void;
};
const Event = observer('Event', ({ userId, event, onHeaderClick }: EventPropsType) => {
    const markets = event.markets.filter((marketModel) => marketModel.trapChallengeTag === 'yes');

    return (
        <section className='trap-challenge__event'>
            <button
                onClick={() => onHeaderClick(null)}
                className='trap-challenge__close-event'
            >
                {event.name}
            </button>
            {markets.map((market) => (
                <div key={market.id}>
                    <div className='trap-challenge__market-name'>{market.name}</div>
                    <ul className='trap-challenge__race-summary'>
                        {market.selections.map((selection) => (
                            <li key={selection.id}>
                                <div className='race-summary__metadata'>
                                    {typeof selection.metaDataNumber === 'string' ? (
                                        <span className='race-summary__number'>{selection.metaDataNumber} - </span>
                                    ) : null}
                                    {selection.name}
                                </div>
                                <Selection
                                    userId={userId}
                                    marketId={selection.marketId}
                                    selectionId={selection.id}
                                >
                                    {selection.price?.f}
                                </Selection>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </section>
    );
});

class CompetitionState {
    public constructor(private readonly props: CompetitionPropsType) {
        makeAutoObservable(this);
    }

    @action public onCompetitionSelect = (): void => {
        this.props.trapChallengeState.onCompetitionSelect(this.props.competition);
        this.props.trapChallengeState.onSelectEvent(null);
    };

    @computed get eventsForCompetition(): Array<EventModel> {
        return this.props.trapChallengeState.allEventsWithTrapChallenge.filter(
            (event) => event.competition === this.props.competition.id
        );
    }
}

type CompetitionPropsType = {
    userId: number;
    competition: CompetitionModel;
    trapChallengeState: TrapChallengeState;
};
const Competition = observer('Competition', (props: CompetitionPropsType) => {
    const [state] = useState(() => new CompetitionState(props));
    const isSelectedCompetition = props.trapChallengeState.selectedCompetition?.id === props.competition.id;

    const className = classNames('trap-challenge__competition', {
        'trap-challenge__competition--open': isSelectedCompetition,
    });

    const content = () => {
        if (isSelectedCompetition === true) {
            const events = state.eventsForCompetition;

            if (props.trapChallengeState.selectedEvent === null) {
                return (
                    <ul className='trap-challenge__events-list'>
                        {events.map((event) => (
                            <li key={event.id}>
                                <button
                                    onClick={() => props.trapChallengeState.onSelectEvent(event)}
                                    className='trap-challenge__event-select'
                                >
                                    {event.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                );
            } else {
                return (
                    <Event
                        userId={props.userId}
                        event={props.trapChallengeState.selectedEvent}
                        onHeaderClick={props.trapChallengeState.onSelectEvent}
                    />
                );
            }
        }
    };

    return (
        <section className={className}>
            <header>
                <button
                    className='trap-challenge__competition-name'
                    onClick={state.onCompetitionSelect}
                >
                    {props.competition.name}
                </button>
            </header>
            {content()}
        </section>
    );
});

type TrapChallengePropsType = {
    userId: number;
};

export const TrapChallenge = observer('TrapChallenge', (props: TrapChallengePropsType) => {
    const appState = useAppStateContext();
    const [state] = useState(() => new TrapChallengeState(appState));
    const events = state.allEventsWithTrapChallenge;

    if (events === null) {
        return null;
    }

    if (state.isLoading) {
        return <span>Loading events...</span>;
    }

    if (events.length === 0) {
        return <span>No trap challenge events found</span>;
    }

    return (
        <div className='trap-challenge'>
            <header>
                <h2>Trap Challenge</h2>
            </header>

            <ul>
                {state.getCompetitionForView.map((competition) => (
                    <li key={competition.id}>
                        <Competition
                            userId={props.userId}
                            competition={competition}
                            trapChallengeState={state}
                        />
                    </li>
                ))}
            </ul>

            <Loading
                message='Loading...'
                active={state.allEventsList() === null}
            />
        </div>
    );
});
